import React, { useEffect, useRef, useState } from 'react';
import { translate } from '../../../services/translate';
import { View } from 'react-native';
import MyDropDownPicker from '../../../components/MyDropDownPicker';
import { faCamera, faLocationDot, faTags } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { NavigationProp, useNavigation } from '@react-navigation/native';
import { CamerasParamList } from '../../../typings/Params';
import { cameraService, GetCamerasMapParameters } from '../../../services/central-api/cameras';
import MyAppText from '../../../components/MyAppText';
import LocationSelector from '../../../components/LocationSelector';
import { centralAPI } from '../../../services/central-api';
import { WindowInformation } from '../../../services/window-information';
import { openInNewTab } from '../../../services/navigation-assistant';

let timer: NodeJS.Timeout;

interface Params {
    setLocation: React.Dispatch<React.SetStateAction<{
        lat: number;
        lng: number;
    } | LatLng | undefined>>;
    setFilters: React.Dispatch<React.SetStateAction<GetCamerasMapParameters>>;
    filters: GetCamerasMapParameters;
}

export default function MapSearchDropdown({ setLocation, filters, setFilters }: Params) {
    const { theme } = useStyles(styleSheet);
    const navigation = useNavigation<NavigationProp<CamerasParamList>>();
    const windowInfo = WindowInformation();

    const [filterSelectorOpen, setFilterSelectorOpen] = useState(false);
    const [selectedSearch, setSelectedSearch] = useState<'cameras' | 'location' | 'tags'>('location');
    const searchSelector = [
        { id: 'cameras', title: translate('cameras'), icon: faCamera },
        { id: 'location', title: translate('location'), icon: faLocationDot },
        { id: 'tags', title: translate('tags'), icon: faTags },
    ];
    const [onSearchCameras, setOnSearchCameras] = useState<{ label: string; value: string; }[]>([]);
    const [cameraTitle, setCameraTitle] = useState<string>('');
    const [cameraTitleLoading, setCameraTitleLoading] = useState<boolean>(false);
    const typingTimeout = useRef<NodeJS.Timeout>();
    const [address, setAddress] = useState<string>('');
    const [selectableTagsFilter, setSelectableTagsFilter] = useState<{ label: string; value: number; }[]>([]);
    const [isTagPickerOpen, setIsTagPickerOpen] = useState(false);
    const [isCameraPickerOpen, setIsCameraPickerOpen] = useState(false);
    const [selectedTags, setSelectedTags] = useState<number[]>([]);

    function goToCamera(id: string, newTab: boolean) {
        if (newTab) {
            openInNewTab('Cameras', { screen: 'CameraPlayer', params: { id } });
            return;
        }
        navigation.navigate('CameraMapPlayer', { id });
    }

    async function setSearchedCameras() {
        try {
            setCameraTitleLoading(true);
            if (cameraTitle.length > 0) {
                const cameras = await cameraService.searchCameras({ textFilter: cameraTitle });
                setOnSearchCameras(cameras.map((camera) => ({
                    label: camera.title,
                    value: camera.id
                })));
            } else {
                setOnSearchCameras([]);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setCameraTitleLoading(false);
        }
    }

    async function getTags() {
        try {
            const tags = await cameraService.getCameraTagsSimplified();
            setSelectableTagsFilter(tags.map((tag) => {
                return {
                    value: tag.id,
                    label: tag.name
                };
            }));
        } catch (err) {
            console.error(err);
        }
    }

    function getPlacesByAddress(delay: number) {
        clearTimeout(timer);

        timer = setTimeout(async () => {
            try {
                if (address) {
                    const response = await centralAPI.getPlaceByAddress(address);
                    const newLocation = { lat: response?.candidates[0]?.geometry?.location.lat, lng: response?.candidates[0]?.geometry?.location.lng };
                    setLocation(newLocation);

                }
            } catch (err) {
                console.error(err);
            }
        }, delay);
    }

    useEffect(() => {
        if (typingTimeout.current) {
            clearTimeout(typingTimeout.current);
        }
        const timeout = setTimeout(async () => {
            setSearchedCameras();
        }, 1000);
        typingTimeout.current = timeout;
        return () => clearTimeout(timeout);
    }, [cameraTitle]);


    useEffect(() => {
        getPlacesByAddress(1000);
    }, [address]);

    useEffect(() => {
        setFilters({ ...filters, tags: selectedTags });
    }, [selectedTags]);

    useEffect(() => {
        getTags();
    }, []);

    return (
        <View pointerEvents='box-none' style={{ flexDirection: windowInfo.isMobile ? 'column' : 'row', gap: 10, flex: 1, justifyContent: 'flex-end' }}>
            <View style={{ width: 150, minHeight: 40, borderRadius: 5, zIndex: 3 }}>
                <MyDropDownPicker
                    height={40}
                    disableBorderRadius={true}
                    open={filterSelectorOpen}
                    setOpen={setFilterSelectorOpen}
                    value={selectedSearch}
                    setValue={setSelectedSearch}
                    items={(Array.isArray(searchSelector) ? searchSelector : []).map((filter) => ({
                        label: filter.title,
                        value: filter.id,
                        icon: () => (
                            <FontAwesomeIcon icon={filter.icon as IconDefinition} fontSize={16} color='#58595B' />
                        ),
                    }))}
                />
            </View>
            <View style={{ width: '100%', minWidth: 250, maxWidth: 600, minHeight: 40, zIndex: 2 }}>
                {selectedSearch === 'cameras' ? (
                    <MyDropDownPicker
                        height={40}
                        placeholderColor={theme.colors.placeholder}
                        placeholder={translate('filter')}
                        open={isCameraPickerOpen}
                        setOpen={setIsCameraPickerOpen}
                        items={onSearchCameras}
                        value={1}
                        setValue={(camera) => {
                            goToCamera(camera(1), false);
                        }}
                        onChangeSearchText={(value: string) => {
                            setCameraTitle(value);
                        }}
                        searchable={true}
                        disableLocalSearch={true}
                        loading={cameraTitleLoading}
                    />) : null
                }
                {selectedSearch === 'location' ?
                    <MyAppText style={{ fontSize: 16, minHeight: 40 }}>
                        <LocationSelector
                            address={address}
                            onAddressChange={setAddress}
                            viewStyle={{ width: '100%' }}
                        />
                    </MyAppText> : null}
                {selectedSearch === 'tags' ? (
                    <MyDropDownPicker
                        height={40}
                        placeholderColor={theme.colors.placeholder}
                        placeholder={translate('filter')}
                        open={isTagPickerOpen}
                        items={selectableTagsFilter}
                        setOpen={setIsTagPickerOpen}
                        value={selectedTags}
                        setValue={setSelectedTags}
                        multiple={true}
                        mode='BADGE'
                        searchable={true}
                    />
                ) : null}
            </View>
        </View>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flex: 1,
        flexDirection: 'row',
    },
    content: {
        flex: 1,
        flexWrap: 'wrap'
    },
    mapTopBar: {
        position: 'absolute',
        flexDirection: 'row',
        zIndex: 1000,
        top: 10,
        paddingHorizontal: 10,
        flexWrap: 'wrap',
        display: 'flex',
        width: '100%',
        justifyContent: 'space-between'
    },
    filters: {
        flexDirection: 'row',
        backgroundColor: theme.colors.field.background,
        borderRadius: 4,
        borderWidth: 1,
        borderColor: theme.colors.field.border,
        maxWidth: 400,
        paddingHorizontal: 8,
        columnGap: 8,
        justifyContent: 'space-evenly'
    },
    newPointOfInterest: {
        width: 40,
        height: 40,
        position: 'absolute',
        zIndex: 2,
        right: 10,
        bottom: 209,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: theme.colors.field.background,
        borderRadius: 4,
        borderWidth: 1,
        borderColor: theme.colors.field.border,
    },
    checkbox: {
        height: 38
    },
}));
